import * as React from 'react';
import { flushSync } from 'react-dom';
import styled from 'styled-components';
import ReportCellComment from '../../pages/Report/components/ReportComments/ReportCellComment';
import { TbClipboardText } from 'react-icons/tb';
import commonStore from '../../store/common';
import { Text, Tooltip } from '@mantine/core';
import { getCellState, isCellEmpty } from '../../utils/cellTemplate';

const isAlphaNumericKey = keyCode => {
  return (
    (keyCode >= 48 && keyCode <= 57) || // 0-9
    (keyCode >= 65 && keyCode <= 90) || // A-Z
    (keyCode >= 97 && keyCode <= 122) // a-z
  );
};

const isNavigationKey = keyCode => {
  return (
    keyCode === 37 || // left
    keyCode === 38 || // up
    keyCode === 39 || // right
    keyCode === 40 || // down
    keyCode === 13 || // enter
    keyCode === 9 // tab
  );
};

const isArrowKey = keyCode => keyCode >= 37 && keyCode <= 40; // left, up, right, down

const keyCodes = {
  POINTER: 32, // space
  ENTER: 13,
  BACKSPACE: 8,
  DELETE: 46
};

const jurisdictionFields = ['reporting_member_state', 'aif_master_feeder_status'];

// A native <select> keeps every <option> in the DOM. In multi-fund reports that
// means tens of thousands of option nodes per section (rows × funds × options).
// Until the user opens the dropdown only the selected option needs to exist, so
// we render just that one and synchronously expand to the full list on the first
// interaction (flushSync, so the options are in place before the native dropdown
// opens). Untouched cells — the vast majority — stay at a single option.
const LazyOptionsSelect = ({
  cell,
  disabled,
  onChange,
  onKeyDown
}: {
  cell: any;
  disabled: boolean;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLSelectElement>) => void;
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const isEmptyValue = isCellEmpty(cell.selectedValue);

  const expand = () => {
    if (!expanded) flushSync(() => setExpanded(true));
  };

  const options = expanded
    ? cell.options
    : cell.options?.filter((item: any) => String(item.value) === String(cell.selectedValue));

  return (
    <SelectStyle
      disabled={disabled}
      style={{
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        color: isEmptyValue ? '#ADB5BD' : '#000',
        background: 'transparent',
        fontSize: '12px',
        height: '100%'
      }}
      data-key={`${cell.dataKey}-${cell.dataIndex}-${cell.tableName}${cell.sub_asset_type ? `-${cell.sub_asset_type}` : ''}`}
      onChange={onChange}
      onCopy={e => e.stopPropagation()}
      onCut={e => e.stopPropagation()}
      onPaste={e => e.stopPropagation()}
      onPointerDown={e => {
        // Must stop propagation: if the pointerdown reaches ReactGrid it starts its
        // own pointer/selection handling and re-renders on the same event, which
        // kills the in-progress native dropdown.
        e.stopPropagation();
        expand();
      }}
      onKeyDown={e => {
        onKeyDown(e);
        // Don't materialise the full option list while arrowing across cells —
        // the user is navigating away, not opening this dropdown.
        if (!isArrowKey(e.keyCode)) expand();
      }}
      onFocus={expand}
      value={cell.selectedValue}
    >
      <option value="">{commonStore.isViewer ? '' : 'Select an option'}</option>
      {options?.map((item: any, index: number) => (
        <option key={`${item.value}-${index}`} value={item.value}>
          {item.label}
        </option>
      ))}
    </SelectStyle>
  );
};

// Owns the per-cell hover latch so the comment icon/popover (heavy DOM + query
// observers) mount only after the user first hovers this cell. Once latched it
// stays mounted; CSS still gates visibility via `:hover`, so the affordance looks
// identical. Cells that already have comments render the icon regardless (handled
// inside ReportCellComment).
const SelectCellView = ({
  cell,
  onChange,
  onKeyDown
}: {
  cell: any;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLSelectElement>) => void;
}) => {
  const [commentMounted, setCommentMounted] = React.useState(false);
  const isEmptyValue = isCellEmpty(cell.selectedValue);
  const hasClipBoardIcon = !!cell.selectedValue && cell.isStaticField;
  const { background } = getCellState(cell, isEmptyValue);
  const isJurisdictionField = jurisdictionFields.includes(cell.dataKey);

  const view = (
    <Wrapper
      hasClipBoardIcon={hasClipBoardIcon}
      style={{ background }}
      onPointerEnter={() => setCommentMounted(true)}
    >
      <SelectContainer className="report-field">
        <div className="select" style={{ height: '100%' }}>
          <LazyOptionsSelect
            cell={cell}
            disabled={
              (!cell.isStaticTemplate && cell.isStaticField) ||
              cell.nonEditable ||
              commonStore.isViewer
            }
            onChange={onChange}
            onKeyDown={onKeyDown}
          />

          {cell?.difference?.is_changed && (
            <Text fz="12px" c="orange.6" style={{ marginLeft: '8px', whiteSpace: 'nowrap' }}>
              Changed
            </Text>
          )}

          {hasClipBoardIcon && <TbClipboardText fontSize="12px" />}
        </div>
        {cell.errorMessage ? (
          <p className="error-message">{cell.errorMessage}</p>
        ) : cell.required && isEmptyValue ? (
          <p className="error-message">This field is mandatory</p>
        ) : (
          ''
        )}
      </SelectContainer>
      <ReportCellComment
        fundId={cell.fundId}
        fieldName={cell.dataKey}
        fundName={cell.fundName}
        tableName={cell.tableName}
        fieldOrder={cell.fieldOrder}
        cellHovered={commentMounted}
      />
    </Wrapper>
  );

  if (!isJurisdictionField) return view;

  return (
    <Tooltip
      label="This field is populated from the Jurisdictions section and is editable exclusively from that page."
      maw="321px"
      multiline
      bg="gray.6"
      fz="12px"
      lh="18px"
      p="8px"
      zIndex={99999}
    >
      {view}
    </Tooltip>
  );
};

export class NewSelectCellTemplate {
  getCompatibleCell(uncertainCell: any) {
    // const text = getCellProperty(uncertainCell, 'selectedValue', 'select')
    // const value = parseFloat(text)
    return { ...uncertainCell };
  }

  handleKeyDown(cell: any, keyCode: number, ctrl: boolean, _shift: boolean, alt: boolean) {
    if (!ctrl && !alt && isAlphaNumericKey(keyCode)) return { cell, enableEditMode: true };
    return {
      cell,
      enableEditMode: keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER
    };
  }

  update(cell: any, cellToMerge: any) {
    // ReactGrid's clear-cell (Delete/Backspace) merges its emptyCell, which
    // carries `type: ""` and no selectedValue. Detect that to reset the value to
    // null; any other merge (choosing an option, paste) carries a real value.
    // Keep the cell's own `type` — otherwise downstream `updateDataByType`
    // (a switch on cell.type) would return undefined and wipe the field.
    const isCleared = cellToMerge.type === '';
    return this.getCompatibleCell({
      ...cell,
      selectedValue: isCleared ? '' : (cellToMerge.selectedValue ?? cellToMerge.text)
    });
  }

  // Extracted inline event handlers - preserving exact same logic
  handleSelectChange =
    (cell: any, onCellChanged: (cell: any, commit: boolean) => void) =>
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onCellChanged(
        {
          ...cell,
          selectedValue: cell.dataKey.includes('reporting_period_year')
            ? +e.currentTarget.value
            : e.currentTarget.value
        },
        true
      );
    };

  handleSelectKeyDown =
    (cell: any, onCellChanged: (cell: any, commit: boolean) => void) =>
    (e: React.KeyboardEvent<HTMLSelectElement>) => {
      // Delete/Backspace: a clicked <select> keeps DOM focus (its pointerdown is
      // stopped so the dropdown can open), so ReactGrid's own clear would target
      // its stale focused cell instead of this one. Clear this cell directly and
      // stop propagation so the grid doesn't also clear the wrong cell.
      if (e.keyCode === keyCodes.DELETE || e.keyCode === keyCodes.BACKSPACE) {
        e.stopPropagation();
        onCellChanged({ ...cell, selectedValue: '' }, true);
        return;
      }
      // Arrow keys must reach ReactGrid (registered as onKeyDown on an ancestor
      // container) so focus can move between cells. Block only the native
      // <select>'s own arrow handling — which would silently change the value
      // while the dropdown is closed — but let the event propagate to navigate.
      if (isArrowKey(e.keyCode)) {
        e.preventDefault();
        return;
      }
      if (isAlphaNumericKey(e.keyCode) || isNavigationKey(e.keyCode)) e.stopPropagation();
    };

  render(cell: any, _isInEditMode: boolean, onCellChanged: (cell: any, commit: boolean) => void) {
    return (
      <SelectCellView
        cell={cell}
        onChange={this.handleSelectChange(cell, onCellChanged)}
        onKeyDown={this.handleSelectKeyDown(cell, onCellChanged)}
      />
    );
  }
}

const SelectStyle = styled.select`
  border: 1px solid transparent;
  width: 100%;
  outline: none;
  display: flex;
  /* flex-direction: column;
  justify-content: center; */
  &:disabled {
    color: #909296 !important;
  }
`;

const Wrapper = styled.div<{ hasClipBoardIcon?: boolean }>`
  width: 100%;
  height: 100%;
  .cell-comment-container {
    // because selector's drop down arrow and clip board icon occupy the right side
    right: ${({ hasClipBoardIcon }) => (hasClipBoardIcon ? 35 : 30)}px;
  }

  &:hover {
    .cell-comment-container {
      visibility: visible;
    }
  }
`;

const SelectContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 4px 8px 4px 4px;

  &:focus-within {
    border: 1px solid #007bff;
  }

  .select {
    display: flex;
    align-items: center;
  }
  .error-message {
    font-size: 12px;
    line-height: 17px;
    padding: 0 4px;
    color: #ff6b6b;
    white-space: initial;
  }
`;
